import './AddRate.less';

import Icon, {
  ArrowRightOutlined,
  LinkOutlined,
  LoadingOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Dropdown,
  Form,
  InputNumber,
  Modal,
  notification,
  Select,
  Spin,
  Tag,
  Tooltip,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCostKmStats } from '../hooks/apiHooks/carrier';
import { useRateCreate } from '../hooks/apiHooks/rate';
import {
  getInAddressTerminals,
  getProvinceFromAddress,
  terminalSetsOverlapOrBothEmpty,
} from '../library/util';
import { Carrier } from '../models/Carrier';
import { Rate } from '../models/Rate';
import { ShipType } from '../models/ShipType';
import { AddressType } from '../models/Terminal';
import carrierService from '../services/carrierService';
import AddressSearchInput from './common/AddressSearchInput';
import ButtonToggle from './common/ButtonToggle';
import AddBusinessSvg from './icons/AddBusinessSvg';
import FuelSurchargeSvg from './icons/FuelSurchargeSvg';
import SettingsEthernetSvg from './icons/SettingsEthernetSvg';
import LinkRateModal from './modals/LinkRateModal';
import OrgDestTerminalAssignModal from './modals/OrgDestTerminalAssignModal';

interface IProps {
  carriers?: Carrier[];
  onGoToRate?: (rateId: number) => void;
  rates?: Rate[];
  selectedCarrier?: Carrier;
  shipTypes: ShipType[];
  showAllCarriers?: boolean;
}

const AddRate: React.FC<IProps> = ({
  carriers,
  onGoToRate,
  rates,
  selectedCarrier,
  shipTypes,
  showAllCarriers,
}) => {
  const createMutation = useRateCreate();
  const [form] = Form.useForm();
  const [currentCarrier, setCurrentCarrier] = useState<Carrier | undefined>(
    selectedCarrier
  );
  const origin = Form.useWatch('origin', form);
  const destination = Form.useWatch('destination', form);
  const modeId = Form.useWatch('modeId', form);
  const classRate = Form.useWatch('classRate', form);
  const { costKmStats } = useCostKmStats(
    currentCarrier?.id ?? 0,
    !!currentCarrier
  );
  const [isTerminalAssignOpened, setIsTerminalAssignOpened] =
    useState<AddressType | null>(null);
  const [originTerminals, setOriginTerminals] = useState<Array<number>>();
  const [destinationTerminals, setDestinationTerminals] =
    useState<Array<number>>();
  const [currentOrigin, setCurrentOrigin] = useState<string>('');
  const [currentDestination, setCurrentDestination] = useState<string>('');
  const [isEstimating, setIsEstimating] = useState(false);
  const [estimatedClassValues, setEstimatedClassValues] = useState<Record<
    string,
    number
  > | null>(null);
  const [estimateConfidence, setEstimateConfidence] = useState<Record<
    string,
    'low' | 'med' | 'high'
  > | null>(null);
  const [isLinkRateModalOpen, setIsLinkRateModalOpen] = useState(false);
  const [linkedRateId, setLinkedRateId] = useState<number | undefined>(
    undefined
  );
  const [isCustomPricing, setIsCustomPricing] = useState(false);

  const handleEstimatePrices = useCallback(async () => {
    if (!currentCarrier || !origin || !destination || !modeId) return;
    setIsEstimating(true);
    try {
      const { estimateConfidence: confidence, estimates } =
        await carrierService.getRateEstimate(currentCarrier.id, {
          destination,
          modeId,
          origin,
        });
      const classRateValues: Record<string, number> = {};
      Object.entries(estimates ?? {}).forEach(([classId, value]) => {
        classRateValues[classId] = value;
      });
      form.setFieldsValue({ classRate: classRateValues });
      setEstimatedClassValues(classRateValues);
      setEstimateConfidence(confidence ?? null);
      notification.success({ message: 'Prices estimated' });
    } catch (err: any) {
      notification.error({
        message:
          err?.response?.data?.message ?? 'Failed to get price estimates',
      });
    } finally {
      setIsEstimating(false);
    }
  }, [currentCarrier, form, modeId, origin, destination]);

  const handleCustomPricing = useCallback(async () => {
    if (!currentCarrier || !origin || !destination || !modeId) return;
    setIsCustomPricing(true);
    try {
      const { price } = await carrierService.getCustomPrice(currentCarrier.id, {
        destination,
        modeId,
        origin,
      });
      const classRateValues: Record<string, number> = {};
      currentCarrier.classes?.forEach((c) => {
        classRateValues[c.id.toString()] = price;
      });
      form.setFieldsValue({ classRate: classRateValues });
      notification.success({ message: 'Custom pricing applied' });
    } catch (err: any) {
      notification.error({
        description:
          err?.response?.data?.message ?? 'Failed to get custom price',
        message: 'Custom pricing failed',
      });
    } finally {
      setIsCustomPricing(false);
    }
  }, [currentCarrier, form, modeId, origin, destination]);

  const findDuplicateRate = useCallback(
    (input: Rate): Rate | undefined => {
      if (!rates) return undefined;
      return rates.find(
        (rate) =>
          rate.origin === input.origin &&
          rate.shipTypeId === input.shipTypeId &&
          rate.destination === input.destination &&
          rate.destinationShipTypeId === input.destinationShipTypeId &&
          rate.modeId === input.modeId &&
          terminalSetsOverlapOrBothEmpty(
            rate.originTerminals,
            input.originTerminals
          ) &&
          terminalSetsOverlapOrBothEmpty(
            rate.destinationTerminals,
            input.destinationTerminals
          )
      );
    },
    [rates]
  );

  const onSubmit = useCallback(
    (input: Rate) => {
      if (currentCarrier) {
        input.carrierId = currentCarrier.id;
      }
      input.active = true;
      input.originTerminals = originTerminals;
      input.destinationTerminals = destinationTerminals;
      if (linkedRateId !== undefined) {
        input.parentRateId = linkedRateId;
      }

      if (estimatedClassValues && input.classRate) {
        const transformed: Record<
          string,
          { isEstimate: boolean; value: number }
        > = {};
        for (const [classId, val] of Object.entries(input.classRate)) {
          const num = Number(val);
          if (!Number.isNaN(num)) {
            transformed[classId] = {
              isEstimate: num === Number(estimatedClassValues[classId] ?? NaN),
              value: num,
            };
          }
        }
        Object.assign(input, { classRate: transformed });
      }

      const existingRate = findDuplicateRate(input);
      if (existingRate) {
        Modal.confirm({
          cancelText: 'Cancel',
          content:
            'A rate with the entered origin, destination, types, mode, and terminals already exists.',
          okText: 'Go To Rate',
          onOk: () => {
            if (onGoToRate) {
              onGoToRate(existingRate.id);
            }
          },
          title: 'Duplicate Rate',
        });
        return;
      }

      createMutation.mutate(input);
    },
    [
      createMutation,
      currentCarrier,
      destinationTerminals,
      estimatedClassValues,
      findDuplicateRate,
      linkedRateId,
      onGoToRate,
      originTerminals,
    ]
  );

  const onFieldsChange = useCallback(
    (changedValues: any, allValues: any) => {
      if (changedValues.carrierId) {
        const newCarrier = carriers?.find(
          (c) => c.id === changedValues.carrierId
        );
        setCurrentCarrier(newCarrier);

        // Clear form except for the carrier selection when carrier changes
        const currentCarrierId = changedValues.carrierId;
        form.resetFields();
        form.setFieldsValue({ carrierId: currentCarrierId });

        // Clear related state
        setOriginTerminals(undefined);
        setDestinationTerminals(undefined);
        setCurrentOrigin('');
        setCurrentDestination('');
        setEstimatedClassValues(null);
        setEstimateConfidence(null);
        setLinkedRateId(undefined);
      }
      if (changedValues.origin) {
        setCurrentOrigin(changedValues.origin);
        setOriginTerminals(undefined);
        setEstimatedClassValues(null);
        setEstimateConfidence(null);
      }
      if (changedValues.destination) {
        setCurrentDestination(changedValues.destination);
        setDestinationTerminals(undefined);
        setEstimatedClassValues(null);
        setEstimateConfidence(null);
      }
      if (changedValues.shipTypeId) {
        setOriginTerminals(undefined);
      }
      if (changedValues.destinationShipTypeId) {
        setDestinationTerminals(undefined);
      }
      if (changedValues.modeId) {
        setEstimatedClassValues(null);
        setEstimateConfidence(null);
      }
    },
    [carriers, form]
  );

  const onTerminalsAssign = useCallback(
    (type: AddressType) => {
      const origin = form.getFieldValue('origin');
      const destination = form.getFieldValue('destination');

      if (
        !currentCarrier ||
        (type === 'origin' && !origin) ||
        (type === 'destination' && !destination)
      ) {
        return;
      }

      setIsTerminalAssignOpened(type);
    },
    [currentCarrier, form]
  );

  const getShipTypesSelect = useCallback(
    (type: AddressType) => {
      let loc = '';
      if (type === 'origin') {
        loc = currentOrigin;
      } else {
        loc = currentDestination;
      }
      let currentTerminals = getInAddressTerminals(
        currentCarrier?.terminals,
        loc
      );
      return (
        <Select showSearch filterOption={filterOption} optionLabelProp="label">
          {shipTypes.map((shipType) => (
            <React.Fragment key={shipType.id}>
              <Select.Option
                key={shipType.id}
                label={shipType.name}
                value={shipType.id}
              >
                <div className="add-rate__ship-type">
                  <span>{shipType.name}</span>
                  {shipType.name === 'Terminal' &&
                    currentTerminals.length > 0 && (
                      <Button
                        icon={<Icon component={AddBusinessSvg} />}
                        type="link"
                        onClick={() => onTerminalsAssign(type)}
                      />
                    )}
                </div>
              </Select.Option>
            </React.Fragment>
          ))}
        </Select>
      );
    },
    [
      onTerminalsAssign,
      currentCarrier,
      shipTypes,
      currentOrigin,
      currentDestination,
    ]
  );

  const handleTerminalsAssign = useCallback(
    (terminals: Array<number>) => {
      if (isTerminalAssignOpened === 'origin') {
        setOriginTerminals(terminals);
      } else {
        setDestinationTerminals(terminals);
      }
      setIsTerminalAssignOpened(null);
    },
    [isTerminalAssignOpened]
  );

  useEffect(() => {
    setCurrentCarrier(selectedCarrier);
    // Reset form when carrier changes via URL navigation or becomes undefined
    form.resetFields();
    setOriginTerminals(undefined);
    setDestinationTerminals(undefined);
    setCurrentOrigin('');
    setCurrentDestination('');
    setEstimatedClassValues(null);
    setEstimateConfidence(null);
    setLinkedRateId(undefined);
  }, [selectedCarrier, form]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      form.resetFields();
      setOriginTerminals(undefined);
      setDestinationTerminals(undefined);
      setEstimatedClassValues(null);
      setEstimateConfidence(null);
      setLinkedRateId(undefined);
    }
  }, [createMutation.isSuccess, form]);

  const availableTerminals = useMemo(() => {
    if (!isTerminalAssignOpened) {
      return [];
    }
    return getInAddressTerminals(
      currentCarrier?.terminals,
      form.getFieldValue(isTerminalAssignOpened)
    );
  }, [currentCarrier?.terminals, form, isTerminalAssignOpened]);

  const selectedTerminals = useMemo(() => {
    if (!isTerminalAssignOpened || !availableTerminals.length) {
      return [];
    }
    const terminalIds =
      isTerminalAssignOpened === 'origin'
        ? originTerminals
        : destinationTerminals;

    if (!terminalIds) {
      return [];
    }

    return availableTerminals.filter((terminal) =>
      terminalIds.includes(terminal.id)
    );
  }, [
    isTerminalAssignOpened,
    availableTerminals,
    originTerminals,
    destinationTerminals,
  ]);

  const getTypeLabel = useCallback(
    (type: AddressType) => {
      const label = type === 'origin' ? 'Origin Type' : 'Destination Type';
      const terminalIds =
        type === 'origin' ? originTerminals : destinationTerminals;

      if (!terminalIds || terminalIds.length === 0) {
        return label;
      }

      const loc = type === 'origin' ? currentOrigin : currentDestination;
      const terminals = getInAddressTerminals(currentCarrier?.terminals, loc);
      const assignedTerminals = terminals.filter((t) =>
        terminalIds.includes(t.id)
      );
      const terminalsTooltip = assignedTerminals
        .map((t) => t.name)
        .filter(Boolean)
        .join(', ');

      return (
        <Tooltip title={terminalsTooltip}>
          <span>
            {label}
            <div className="terminal-indicator" style={{ marginLeft: '6px' }} />
          </span>
        </Tooltip>
      );
    },
    [
      originTerminals,
      destinationTerminals,
      currentOrigin,
      currentDestination,
      currentCarrier,
    ]
  );

  const filterOption = (input: any, option: any) => {
    return (option?.label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  const isEstimateButtonEnabled = useMemo(() => {
    if (!origin || !destination || !modeId) return false;
    const orgProv = getProvinceFromAddress(origin);
    const destProv = getProvinceFromAddress(destination);
    if (!orgProv || !destProv || !costKmStats?.length) return false;
    const corridorRows = costKmStats.filter(
      (row) =>
        row.orgProv === orgProv &&
        row.destProv === destProv &&
        row.modeId === modeId
    );
    if (!corridorRows.length) return false;
    const hasAcceptableStats = corridorRows.some(
      (row) =>
        (row.stddev == null || row.stddev < 0.6) &&
        (row.sampleCount == null || row.sampleCount > 4)
    );
    return hasAcceptableStats;
  }, [origin, destination, modeId, costKmStats]);

  const supportedCorridorsForEstimate = useMemo(() => {
    if (!costKmStats?.length || !currentCarrier?.modes) return [];
    const corridorMap = new Map<string, Set<number>>();
    for (const row of costKmStats) {
      if (
        (row.stddev == null || row.stddev < 0.6) &&
        (row.sampleCount == null || row.sampleCount > 4)
      ) {
        const key = `${row.orgProv} → ${row.destProv}`;
        if (!corridorMap.has(key)) corridorMap.set(key, new Set());
        corridorMap.get(key)!.add(row.modeId);
      }
    }
    return Array.from(corridorMap.entries()).map(([corridor, modeIds]) => ({
      corridor,
      modeIds: Array.from(modeIds),
    }));
  }, [costKmStats, currentCarrier?.modes]);

  const carrierFuelSurcharge = currentCarrier?.option?.fuelSurcharge ?? 0;

  return (
    <>
      <Form
        className="add-rate"
        form={form}
        initialValues={{ isOnRoute: true }}
        onFinish={onSubmit}
        onValuesChange={onFieldsChange}
      >
        <Card>
          <div className="add-rate__layout">
            <div className="add-rate__row">
              {showAllCarriers && (
                <div className="add-rate__field">
                  <div className="add-rate__label">Carrier</div>
                  <Form.Item
                    name="carrierId"
                    rules={[{ message: 'Required', required: true }]}
                  >
                    <Select showSearch filterOption={filterOption}>
                      {carriers?.map((carrier) => (
                        <Select.Option
                          key={carrier.id}
                          label={carrier.name}
                          value={carrier.id}
                        >
                          {carrier.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}

              <div className="add-rate__field add-rate__field--md">
                <div className="add-rate__label">Origin</div>
                <Form.Item
                  name="origin"
                  rules={[{ message: 'Required', required: true }]}
                >
                  <AddressSearchInput />
                </Form.Item>
              </div>

              <div className="add-rate__field add-rate__field--type">
                <div className="add-rate__label">{getTypeLabel('origin')}</div>
                <Form.Item
                  name="shipTypeId"
                  rules={[{ message: 'Required', required: true }]}
                >
                  {getShipTypesSelect('origin')}
                </Form.Item>
              </div>

              <div aria-hidden="true" className="add-rate__arrow">
                <ArrowRightOutlined />
              </div>

              <div className="add-rate__field add-rate__field--md">
                <div className="add-rate__label">Destination</div>
                <Form.Item
                  name="destination"
                  rules={[{ message: 'Required', required: true }]}
                >
                  <AddressSearchInput />
                </Form.Item>
              </div>

              <div className="add-rate__field add-rate__field--type">
                <div className="add-rate__label">
                  {getTypeLabel('destination')}
                </div>
                <Form.Item
                  name="destinationShipTypeId"
                  rules={[{ message: 'Required', required: true }]}
                >
                  {getShipTypesSelect('destination')}
                </Form.Item>
              </div>

              {currentCarrier && (
                <div className="add-rate__field add-rate__field--mode">
                  <div className="add-rate__label">Mode</div>
                  <Form.Item
                    name="modeId"
                    rules={[{ message: 'Required', required: true }]}
                  >
                    <Select showSearch filterOption={filterOption}>
                      {currentCarrier.modes
                        ?.filter((mode) => mode.active)
                        .map((mode) => (
                          <Select.Option
                            key={mode.id}
                            label={mode.name}
                            value={mode.id}
                          >
                            {mode.name}
                          </Select.Option>
                        ))}
                    </Select>
                  </Form.Item>
                </div>
              )}

              {currentCarrier && (
                <div className="add-rate__field add-rate__field--estimate">
                  <div className="add-rate__label">
                    Actions
                    {linkedRateId && (
                      <Tooltip title="Currently Linked to a Parent Rate">
                        <span className="add-rate__linked-indicator" />
                      </Tooltip>
                    )}
                  </div>
                  <div
                    style={{ alignItems: 'center', display: 'flex', gap: 8 }}
                  >
                    <Dropdown.Button
                      disabled={
                        currentCarrier?.id === 42
                          ? !origin ||
                            !destination ||
                            !modeId ||
                            isEstimating ||
                            isCustomPricing
                          : false
                      }
                      menu={{
                        items: [
                          {
                            disabled:
                              !isEstimateButtonEnabled ||
                              !!linkedRateId ||
                              isEstimating ||
                              isCustomPricing,
                            key: 'estimate-prices',
                            label: (
                              <Tooltip
                                title={
                                  !isEstimateButtonEnabled ? (
                                    <div
                                      style={{
                                        maxHeight: 180,
                                        overflowY: 'auto',
                                        paddingRight: 8,
                                      }}
                                    >
                                      <div style={{ marginBottom: 8 }}>
                                        Supported for estimation
                                      </div>
                                      {supportedCorridorsForEstimate.length ? (
                                        supportedCorridorsForEstimate.map(
                                          ({ corridor, modeIds }) => (
                                            <div
                                              key={corridor}
                                              style={{ marginBottom: 6 }}
                                            >
                                              <strong>{corridor}:</strong>{' '}
                                              {modeIds
                                                .map(
                                                  (id) =>
                                                    currentCarrier?.modes?.find(
                                                      (m) => m.id === id
                                                    )?.name ?? String(id)
                                                )
                                                .join(', ')}
                                            </div>
                                          )
                                        )
                                      ) : (
                                        <div>
                                          No corridors with sufficient data yet
                                        </div>
                                      )}
                                    </div>
                                  ) : undefined
                                }
                              >
                                <span>Estimate Prices</span>
                              </Tooltip>
                            ),
                            onClick: handleEstimatePrices,
                          },
                          ...(currentCarrier?.id !== 42
                            ? [
                                {
                                  disabled:
                                    !origin ||
                                    !destination ||
                                    !modeId ||
                                    isEstimating ||
                                    isCustomPricing,
                                  key: 'use-custom-pricing',
                                  label: 'Use Custom Pricing',
                                  onClick: handleCustomPricing,
                                },
                              ]
                            : [
                                {
                                  disabled:
                                    !!linkedRateId ||
                                    isEstimating ||
                                    isCustomPricing,
                                  key: 'link-parent',
                                  label: 'Link Parent',
                                  onClick: () => setIsLinkRateModalOpen(true),
                                },
                              ]),
                        ],
                      }}
                      trigger={['click']}
                      onClick={
                        currentCarrier?.id === 42
                          ? handleCustomPricing
                          : () => setIsLinkRateModalOpen(true)
                      }
                    >
                      {currentCarrier?.id === 42 ? (
                        'Use Custom Pricing'
                      ) : (
                        <>
                          <LinkOutlined
                            style={{
                              color: linkedRateId ? '#1890ff' : undefined,
                              marginRight: 6,
                            }}
                          />
                          Link Parent
                        </>
                      )}
                    </Dropdown.Button>
                    {(isEstimating || isCustomPricing) && (
                      <Spin
                        indicator={
                          <LoadingOutlined spin style={{ fontSize: 16 }} />
                        }
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {currentCarrier && (
              <div className="add-rate__row add-rate__row--details">
                <div className="add-rate__classes">
                  {currentCarrier.classes?.map((classification) => (
                    <div
                      key={classification.id}
                      className="add-rate__field add-rate__field--class"
                    >
                      <div className="add-rate__label">
                        <Tooltip title={classification.name}>
                          <span className="add-rate__truncate">
                            {classification.name}
                          </span>
                        </Tooltip>
                        {estimateConfidence?.[classification.id] && (
                          <Tooltip
                            title={
                              estimateConfidence[classification.id] === 'low'
                                ? 'Low confidence estimate'
                                : estimateConfidence[classification.id] ===
                                  'med'
                                ? 'Medium confidence estimate'
                                : 'High confidence estimate'
                            }
                          >
                            <span
                              className={`add-rate__confidence-indicator add-rate__confidence-indicator--${
                                estimateConfidence[classification.id]
                              }`}
                            />
                          </Tooltip>
                        )}
                      </div>
                      <Form.Item
                        className={
                          estimatedClassValues &&
                          Number(classRate?.[classification.id]) ===
                            estimatedClassValues[classification.id]
                            ? 'add-rate__class-rate--estimated'
                            : undefined
                        }
                        name={['classRate', classification.id.toString()]}
                      >
                        <InputNumber
                          controls={false}
                          readOnly={!!linkedRateId}
                          type="number"
                        />
                      </Form.Item>
                    </div>
                  ))}
                </div>

                <div className="add-rate__actions">
                  <div className="add-rate__fuel">
                    <Form.Item hidden name="skipFuelSurcharge" />
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue, setFieldsValue }) => {
                        const skipFuel = !!getFieldValue('skipFuelSurcharge');
                        const inheritFuel = !skipFuel;
                        return (
                          <Tooltip
                            title={
                              inheritFuel
                                ? 'Remove fuel surcharge'
                                : 'Apply fuel surcharge'
                            }
                          >
                            <Tag
                              color={inheritFuel ? 'green' : 'default'}
                              icon={<Icon component={FuelSurchargeSvg} />}
                              style={{ cursor: 'pointer' }}
                              onClick={() =>
                                setFieldsValue({
                                  skipFuelSurcharge: inheritFuel,
                                })
                              }
                            >
                              Fuel ({carrierFuelSurcharge}%) {''}
                              {inheritFuel ? 'applied' : 'not applied'}
                            </Tag>
                          </Tooltip>
                        );
                      }}
                    </Form.Item>
                  </div>

                  <div className="add-rate__action">
                    <Form.Item name="transitional">
                      <ButtonToggle
                        icon={<Icon component={SettingsEthernetSvg} />}
                        tooltip="Transitional"
                      />
                    </Form.Item>
                  </div>

                  <div className="add-rate__action">
                    <Form.Item name="isOnRoute">
                      <ButtonToggle
                        icon={<NodeIndexOutlined />}
                        tooltip="On route"
                      />
                    </Form.Item>
                  </div>

                  <Form.Item>
                    <Button
                      className="add-rate__submit-button"
                      htmlType="submit"
                      loading={createMutation.isLoading}
                      type="primary"
                    >
                      Add Rate
                    </Button>
                  </Form.Item>
                </div>
              </div>
            )}
          </div>
        </Card>
      </Form>

      {isLinkRateModalOpen && (
        <LinkRateModal
          classes={currentCarrier?.classes}
          fuelSurchargePercent={currentCarrier?.option?.fuelSurcharge}
          linkedRateId={linkedRateId}
          modes={currentCarrier?.modes}
          rates={rates ?? []}
          shipTypes={shipTypes}
          onClose={() => setIsLinkRateModalOpen(false)}
          onConfirm={(rateId) => {
            setLinkedRateId(rateId ?? undefined);
            if (currentCarrier?.classes) {
              if (rateId != null && rates) {
                const linkedRate = rates.find((r) => r.id === rateId);
                const classRateValues: Record<string, number> = {};
                currentCarrier.classes.forEach((c) => {
                  const value = linkedRate?.classRate?.[c.id]?.value;
                  if (value != null) {
                    classRateValues[c.id.toString()] = value;
                  }
                });
                form.setFieldsValue({ classRate: classRateValues });
              } else {
                const clearedClassRate = Object.fromEntries(
                  currentCarrier.classes.map((c) => [
                    c.id.toString(),
                    undefined,
                  ])
                );
                form.setFieldsValue({ classRate: clearedClassRate });
              }
            }
            setEstimatedClassValues(null);
            setEstimateConfidence(null);
            setIsLinkRateModalOpen(false);
          }}
        />
      )}
      {isTerminalAssignOpened && availableTerminals.length > 0 && (
        <OrgDestTerminalAssignModal
          initialTerminals={selectedTerminals}
          terminals={availableTerminals}
          type={isTerminalAssignOpened}
          onClose={() => setIsTerminalAssignOpened(null)}
          onSubmit={handleTerminalsAssign}
        />
      )}
    </>
  );
};

export default AddRate;
